import { useState } from 'react';
import { NJButton, NJText } from '@engie-group/fluid-design-system-react';
import { UMFormStyles, UMSelect, UMR } from '../../urbanmap/_components';
import type { PlanGeoData } from '../proposition-c/types';
import { MapPane, type MapOverlay } from './MapPane';
import { PlanRecap } from './PlanRecap';
import { PhasingWorkspace } from './PhasingWorkspace';
import { ContextSets } from './ContextSets';
import { BuildingsSection } from './BuildingsSection';
import { PlanCharts } from './PlanCharts';
import { routeOf } from './construction';
import { consumption, segmentKm, type CalculationSection } from './sectionEngine';
import { setsOf } from './model';
import type { Family, Plan, PlanBrick, Project } from './types';
import { fmt } from './ui';

interface Props {
  project: Project; plan: Plan; geo?: PlanGeoData; canWrite: boolean; dirty: boolean; active: PlanBrick;
  onUpdate: (changes: Partial<Plan>) => void;
  onOpenSet: (family: Family, id: string) => void;
  onRun: (section: CalculationSection) => void; onSST: () => void; onExport: () => void;
}

export function PlanBricks(p: Props) {
  const [fullSection, setFullSection] = useState<PlanBrick | null>(null);
  const full = fullSection === p.active;
  const [area, setArea] = useState(false);
  const [message, setMessage] = useState('');
  const buildingIds = p.plan.buildingIds ?? [];
  const sets = setsOf(p.project, p.plan);
  const route = routeOf(p.plan, p.geo?.pipes.features.length ?? 0);
  const km = route.indexes.reduce((sum, index) => sum + segmentKm(p.geo, index), 0);
  const mwh = consumption(p.project, p.plan);
  const overlay: MapOverlay = {
    editAllPipes: p.active === 'trace' && p.plan.routeMode === 'manual',
    selectedBuildings: buildingIds, routePipeIndexes: route.indexes,
    technicalPoints: p.project.technicalPoints?.filter((point) => point.status === 'validated'),
    startPoints: p.active === 'trace' ? sets?.pointsDepart.startPoints : 0,
    forbiddenRoads: p.active === 'trace' ? sets?.trace.forbiddenRoads : 0,
    mandatoryRoads: p.active === 'trace' ? sets?.trace.mandatoryRoads : 0,
    weightedRoads: p.active === 'trace' ? sets?.trace.weightedRoads : 0,
    areaMode: area && p.canWrite && p.active === 'buildings', onAreaError: setMessage,
    onAreaSelect: ({ buildings }) => {
      p.onUpdate({ buildingIds: [...new Set([...buildingIds, ...buildings])] }); setArea(false);
      setMessage(`${buildings.length} bâtiments dans la zone, ajoutés sans doublon au brouillon.`);
    },
    onBuildingClick: p.canWrite && p.active === 'buildings'
      ? (id) => p.onUpdate({ buildingIds: buildingIds.includes(id) ? buildingIds.filter((item) => item !== id) : [...buildingIds, id] }) : undefined,
    onPipeClick: p.canWrite && p.active === 'trace' && p.plan.routeMode === 'manual' ? (index) => p.onUpdate({
      routePipeIndexes: route.indexes.includes(index) ? route.indexes.filter((i) => i !== index) : [...route.indexes, index],
    }) : undefined,
    legend: p.active === 'trace'
      ? ['Orange : tracé simulé du plan · vert : départ réseau (démonstration).',
        'Rouge : interdit · vert : obligatoire · jaune : poids ×2 simulé. SST projet distincts des départs.']
      : ['Vert : bâtiments du plan actif (prioritaire sur les sélections).', 'Cliquez pour ajouter/retirer ; lasso = zone dessinée.'],
  };
  if (p.active === 'recap') return <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
    <PlanRecap project={p.project} plan={p.plan} geo={p.geo} dirty={p.dirty} onExport={p.onExport} />
  </div>;
  const canFull = p.active === 'buildings' || p.active === 'phasing';
  return <div style={{ display: 'flex', flex: 1, minHeight: 0, flexDirection: 'column' }}>
    {canFull && <div style={{ padding: '6px 12px', borderBottom: `1px solid ${UMR.border}` }}>
      <NJButton label={full ? 'Retour à la carte' : 'Vue complète'} variant="secondary" scale="sm" onClick={() => {
        setFullSection(full ? null : p.active); setArea(false);
      }} />
    </div>}
    {full && canFull ? <section style={{ overflowY: 'auto', flex: 1, padding: 20 }}>
      <ContextSets {...p} families={p.active === 'buildings' ? ['demande', 'importance'] : ['developpement', 'phasage']} />
      <PlanCharts project={p.project} plan={p.plan} geo={p.geo} kind={p.active === 'buildings' ? 'buildings' : 'phasing'} />
    </section> : null}
    {p.active === 'phasing' ? <div style={{ display: full ? 'none' : 'flex', flex: 1, minHeight: 0 }}>
      <PhasingWorkspace {...p} />
    </div> : <div style={{ display: full && canFull ? 'none' : 'flex', flex: 1, minHeight: 0 }}>
      <section style={{ flex: '0 0 355px', overflowY: 'auto', padding: 14, borderRight: `1px solid ${UMR.border}` }}>
        {p.active === 'buildings' ? <>
          <BuildingsSection {...p} total={mwh} />
          {p.canWrite && <NJButton label={area ? 'Annuler le lasso' : 'Ajouter par lasso'} variant="secondary" scale="sm" onClick={() => setArea(!area)} />}
          {message && <NJText scale="xs" role="status">{message}</NJText>}
        </> : <>
          <NJText as="h3" scale="md">Tracé du réseau</NJText>
          <ContextSets {...p} families={['trace', 'pointsDepart']} />
          <UMFormStyles />
          <fieldset disabled={!p.canWrite} style={{ border: 0, margin: '12px 0', padding: 0 }}>
            <UMSelect value={p.plan.routeMode ?? 'auto'} ariaLabel="Mode de tracé" onChange={(v) => p.onUpdate({ routeMode: v as Plan['routeMode'] })}
              options={[{ value: 'auto', label: 'Automatique — simulation locale' }, { value: 'manual', label: 'Manuel — cliquer les tronçons' }]} />
          </fieldset>
          <NJText scale="xs">Démonstration : sous-ensemble des tronçons Gentilly, sans optimisation de raccordement. Aucun moteur métier.</NJText>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <NJButton label="Lancer le tracé" disabled={!p.canWrite || p.dirty || !p.plan.buildingsConfirmed || !buildingIds.length}
              scale="sm" onClick={() => p.onRun('trace')} />
            <NJButton label="Calculer la densité" disabled={!p.canWrite || p.dirty || !p.plan.traceCalculated}
              scale="sm" onClick={() => p.onRun('density')} />
          </div>
          <NJText scale="xs">La densité inclut une correction automatique de géométrie simulée (contrôle local de longueur, pas de correction métier).</NJText>
          {p.plan.densityCorrection && <NJText scale="xs" role="status">{p.plan.densityCorrection}</NJText>}
          {p.dirty && <NJText scale="xs" role="status">Confirmez les modifications avant le calcul local.</NJText>}
          {!p.plan.buildingsConfirmed && <NJText scale="xs">Confirmez les bâtiments dans leur étape avant de lancer le tracé.</NJText>}
          <NJText scale="sm">Longueur : {fmt(km, 3)} km</NJText>
          <NJText scale="sm">Densité : {km ? fmt(mwh / (km * 1000), 2) : '—'} MWh/m/an</NJText>
          <NJText scale="xs">Longueur géodésique des tronçons affichés. Densité = consommation annuelle / mètres de réseau ; recalculée après retrait d’un prospect.</NJText>
        </>}
      </section>
      {p.geo ? <MapPane geo={p.geo} overlay={overlay} /> : <NJText role="status">Chargement de la carte…</NJText>}
    </div>}
  </div>;
}
